/**
 * Normalisation of personalised data.
 *
 * The previous version of the app stored, per user, JSON arrays shaped like:
 *   categories: [{ name, icon, color }]
 *   symbols:    [{ text: { pt, en }, category: "<category name>", icon, image }]
 * without ids. These helpers accept both that legacy shape and the current one,
 * drop entries that cannot be repaired and return data that passes the schemas.
 * They never throw: bad input yields an empty or partial result.
 */
import { LIMITS } from './constants';
import {
  DEFAULT_SETTINGS,
  customCategorySchema,
  customSymbolSchema,
  routineSchema,
  imageDataUrlSchema,
  settingsSchema,
  usageEntrySchema,
  type CustomCategory,
  type CustomSymbol,
  type Hidden,
  type Routine,
  type Settings,
  type Usage,
} from './schemas';
import { BASE_CATEGORY_IDS, baseCategoryIdFromName } from './symbols';

const DEFAULT_CATEGORY_ICON = '⭐';
const DEFAULT_CATEGORY_COLOR = '#4f46e5';
const DEFAULT_SYMBOL_ICON = '💬';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/** Deterministic id for a legacy category, derived from its name. */
export function legacyCategoryId(name: string): string {
  const slug = name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
  return `legacy-${slug || hash(name)}`;
}

function hash(input: string): string {
  let h = 5381;
  for (let i = 0; i < input.length; i++) h = ((h << 5) + h + input.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

export function normalizeCategories(raw: unknown): CustomCategory[] {
  if (!Array.isArray(raw)) return [];
  const result: CustomCategory[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!isRecord(item)) continue;
    const name = str(item.name)?.slice(0, LIMITS.maxCategoryNameLength);
    if (!name) continue;
    let id = str(item.id) ?? legacyCategoryId(name);
    // A custom category must never shadow a built-in one.
    if (BASE_CATEGORY_IDS.has(id)) id = `custom-${id}`;
    if (seen.has(id)) continue;
    const candidate = {
      id,
      name,
      icon: str(item.icon) ?? DEFAULT_CATEGORY_ICON,
      color: typeof item.color === 'string' && /^#[0-9a-fA-F]{6}$/.test(item.color) ? item.color : DEFAULT_CATEGORY_COLOR,
    };
    const parsed = customCategorySchema.safeParse(candidate);
    if (!parsed.success) continue;
    seen.add(id);
    result.push(parsed.data);
    if (result.length >= LIMITS.maxCategories) break;
  }
  return result;
}

export function normalizeSymbols(raw: unknown, categories: CustomCategory[]): CustomSymbol[] {
  if (!Array.isArray(raw)) return [];
  const byName = new Map(categories.map((c) => [c.name.toLowerCase(), c.id]));
  const categoryIds = new Set(categories.map((c) => c.id));
  const result: CustomSymbol[] = [];
  const seen = new Set<string>();

  raw.forEach((item, index) => {
    if (!isRecord(item) || result.length >= LIMITS.maxSymbols) return;

    const text = isRecord(item.text) ? item.text : {};
    const pt = str(text.pt) ?? str(text.en);
    const en = str(text.en) ?? pt;
    if (!pt || !en) return;

    const legacyName = str(item.category);
    let categoryId = str(item.categoryId);
    if (!categoryId || (!BASE_CATEGORY_IDS.has(categoryId) && !categoryIds.has(categoryId))) {
      categoryId = legacyName
        ? (baseCategoryIdFromName(legacyName) ?? byName.get(legacyName.toLowerCase()) ?? legacyCategoryId(legacyName))
        : undefined;
    }
    if (!categoryId) return;

    let id = str(item.id) ?? `legacy-${index}-${hash(pt + categoryId)}`;
    if (seen.has(id)) id = `${id}-${index}`;

    const image = imageDataUrlSchema.safeParse(item.image);
    const candidate = {
      id,
      categoryId,
      ...(legacyName ? { category: legacyName.slice(0, LIMITS.maxCategoryNameLength) } : {}),
      text: { pt: pt.slice(0, LIMITS.maxLabelLength), en: en.slice(0, LIMITS.maxLabelLength) },
      icon: str(item.icon)?.slice(0, 16) ?? DEFAULT_SYMBOL_ICON,
      image: image.success ? image.data : null,
    };
    const parsed = customSymbolSchema.safeParse(candidate);
    if (!parsed.success) return;
    seen.add(id);
    result.push(parsed.data);
  });
  return result;
}

/** Keeps only valid usage entries (drops anything malformed, never throws). */
export function normalizeUsage(raw: unknown): Usage {
  if (!isRecord(raw)) return {};
  const entries = Object.entries(raw).filter(([key, value]) => key.length <= 64 && usageEntrySchema.safeParse(value).success) as [
    string,
    Usage[string],
  ][];
  return Object.fromEntries(entries.sort((a, b) => b[1].t - a[1].t).slice(0, LIMITS.maxUsageEntries));
}

export function normalizeSettings(raw: unknown): Settings {
  if (!isRecord(raw)) return { ...DEFAULT_SETTINGS };
  const merged = { ...DEFAULT_SETTINGS };
  for (const key of Object.keys(DEFAULT_SETTINGS) as (keyof Settings)[]) {
    const candidate = { ...merged, [key]: key === 'gridSize' || key === 'voiceSpeed' ? Number(raw[key]) : raw[key] };
    if (raw[key] !== undefined && settingsSchema.safeParse(candidate).success) Object.assign(merged, candidate);
  }
  return merged;
}

const validId = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0 && v.length <= 64;

/** Keeps valid, distinct ids only (never throws). */
export function normalizeHidden(raw: unknown): Hidden {
  if (!isRecord(raw)) return { symbols: [], categories: [] };
  const ids = (list: unknown, max: number) => (Array.isArray(list) ? [...new Set(list.filter(validId))].slice(0, max) : []);
  return { symbols: ids(raw.symbols, LIMITS.maxHiddenSymbols), categories: ids(raw.categories, LIMITS.maxHiddenCategories) };
}

/** Drops malformed routines and repeated ids (never throws). */
export function normalizeRoutines(raw: unknown): Routine[] {
  if (!Array.isArray(raw)) return [];
  const result: Routine[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const parsed = routineSchema.safeParse(item);
    if (!parsed.success || seen.has(parsed.data.id)) continue;
    seen.add(parsed.data.id);
    result.push(parsed.data);
    if (result.length >= LIMITS.maxRoutines) break;
  }
  return result;
}
